import React, {useState, useContext} from 'react';
import {useAnnotation} from '../../Annotations/AnnotationContext';
import {makeStyles} from '@material-ui/core';
import {usePlantSelection} from './PlantSelectionContext';
import {Snackbar} from '@mui/material';
import {UserContext} from '../../../contexts/userContext';
import generateHeatmapColor from '../../../Utils/HeatmapUtils';
import '../PlantBoxes.css';

const useStyles = makeStyles((theme) => ({
  hover: {
    '&:hover': {
      'backgroundColor': 'rgba(169, 169, 169, 0.5)',
    },
    'padding': theme.spacing(1),
  },
  flexVertical: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexVerticalBottomItem: {
    justifyContent: 'center',
  },
  flexVerticalFiller: {
    justifyContent: 'center',
    flexGrow: 1,
  },
  smallText: {
    fontSize: '12px', lineHeight: 'normal', color: 'white',
  },
  titleText: {
    width: '100%',
    fontSize: '14px',
    lineHeight: 'normal',
    color: 'white',
  },
}));

function divBorderStyle(annotation, color, downscalingRatio) {
  if (!annotation?.pixel_dimensions) {
    return {};
  }
  const center = annotation.center_coordinate.map((item) => item / downscalingRatio);
  const dimensions = annotation.pixel_dimensions.map((item) => item / downscalingRatio);
  const left = Math.round(center[0] - dimensions[0] / 2);
  const top = Math.round(center[1] - dimensions[1] / 2);
  const styleDict = {
    position: 'absolute',
    left: left + 'px',
    top: top + 'px',
    width: dimensions[0] + 'px',
    height: dimensions[1] + 'px',
    border: '2px solid ' + color,
    boxSizing: 'border-box',
  };
  return styleDict;
}


function PlantDiv({plant, downscalingRatio, snapshotId, setPlantInDropDown}) {
  const {getPlantAnnotation, pendingAnnotations} = useAnnotation();
  const {setSelectedPlant, selectedPlant} = usePlantSelection();
  const {getUserConfig} = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const getColor = (target) => {
    if (target?.healthy) {
      return 'green';
    } else {
      return 'pink';
    }
  };

  const getHeatmapBackgroundColor = () => {
    const config = getUserConfig(true);
    if (!config?.plant_heatmap) {
      return null;
    }

    // Map config value to plant attribute
    let heatmapValue;
    switch (config.plant_heatmap) {
      case 'leaf_area_index':
        heatmapValue = plant.leaf_area_index;
        break;
      case 'height':
        heatmapValue = plant.height ? plant.height : undefined;
        break;
      default:
        return null;
    }

    if (heatmapValue === undefined) {
      return null;
    }

    // Normalize and square the value for better color distribution, more in line with Stevens' Power Law.
    const normalizedValue = Math.max(0, Math.min(1, heatmapValue**2));
    return generateHeatmapColor(normalizedValue, 0, 1, 0.6);
  };

  const convertTargetToRectangleStyle = () => {
    const color = getColor(plant);
    const styleDict = divBorderStyle(plant, color, downscalingRatio);
    if (selectedPlant?.plant_id === plant.plant_id) {
      styleDict['backgroundColor'] = 'rgba(35, 164, 85, 0.5)';
    } else {
      const heatmapColor = getHeatmapBackgroundColor();
      if (heatmapColor) {
        styleDict['backgroundColor'] = heatmapColor;
      }
    }
    return styleDict;
  };

  const getTitleText = () => {
    let text = '';
    if (plant?.disease_classifications?.length > 0) {
      for (const disease of plant.disease_classifications) {
        text += disease.label + ' ' + Math.round(disease.confidence*100) + '%\n';
      }
    }
    if (text === '') {
      text = plant.plant_id;
    }
    text = text.charAt(0).toUpperCase() + text.slice(1);
    return text;
  };

  const getBottomText = () => {
    let text = '';
    const annotation = getPlantAnnotation(plant);
    if (annotation) {
      text += ' annotation';
      if (annotation?.label) {
        text += ': ' + annotation.label;
      } else {
        text += ': scheduled';
      }
    } else if (pendingAnnotations.includes(plant.plant_id)) {
      text += ' annotation: pending';
    }
    return text;
  };

  const getAverageBudSize = () => {
    return plant.buds && plant.buds.length > 0 ?
        ((plant.buds.reduce((acc, bud) => acc + bud.diameter, 0) / plant.buds.length) / 10).toFixed(2) :
        0;
  };

  const getBiggestBudSize = () => {
    return plant.buds && plant.buds.length > 0 ?
    ((plant.buds.reduce((acc, bud) => Math.max(acc, bud.diameter), 0)) / 10).toFixed(2) :
    0;
  };

  const getClipBoardText = () => {
    const plantId = plant.plant_id;
    const plantLength = plant.dimensions?.[0]?.toFixed(2) || '';
    const plantWidth = plant.dimensions?.[1]?.toFixed(2) || '';
    const plantHeight = plant.height?.toFixed(2) || '';
    const budCount = plant.buds.length;
    const averageBudSize = getAverageBudSize() === 0 ? '' : getAverageBudSize();
    const biggestBudSize = getBiggestBudSize() === 0 ? '' : getBiggestBudSize();
    return `${snapshotId},${plantId},${plantLength},${plantWidth},${plantHeight},${budCount},` +
           `${averageBudSize},${biggestBudSize}`;
  };

  const onClick = () => {
    const text = getClipBoardText();
    navigator.clipboard.writeText(text);
    setOpen(true);
    setSelectedPlant(plant);
    setPlantInDropDown(plant.plant_id);
  };

  return (
    <div style={convertTargetToRectangleStyle()} className='plant-box'
      onClick={onClick}>
      <div className='plant-box-tooltip'>
        <p >Height: {(plant.height*100).toFixed(1)} cm</p>
        {plant.dimensions &&
        <p >Dimensions: {parseInt(plant.dimensions[0]*100)} cm x {parseInt(plant.dimensions[1]*100)} cm</p>}
        <p >Buds: {plant.buds.length}</p>
        <p>LAI: {(plant.leaf_area_index*100).toFixed(0)}%</p>
      </div>
      <span className={`${classes.titleText} ${classes.flexVerticalFiller}`}>
        {getTitleText()}
      </span>
      <span className={`${classes.flexVerticalBottomItem} ${classes.smallText}`}>
        {getBottomText()}
      </span>
      <Snackbar
        open={open}
        onClose={() => setOpen(false)}
        autoHideDuration={2000}
        message="Copied plant ID to clipboard"
      />
    </div>
  );
}

export default PlantDiv;
